// Types
import { ResizeBorderHorizontal } from '@src/components/common/ResizeBorder';
import DebugModal from '@src/components/debug/DebugModal';
import { useTimeline } from '@src/hooks/timeline/useTimeline';
import { useTimelineTracks } from '@src/hooks/timeline/useTimelineTracks';
import { useTimelineLayoutResize } from '@src/hooks/useTimelineLayoutResize';
import { useLayoutStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { usePinch } from '@use-gesture/react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DraggableCore } from 'react-draggable';
import styled from 'styled-components';

import TimelineControls from './controls/TimelineControls';
import { TimelineHeader } from './TimelineHeader';
import { TimelineTrackLabels } from './TimelineTrackLabels';
import { TimelineTracks } from './TimelineTracks';

import type { ElementState } from '@src/renderer/ElementState';
import type { Story } from '@src/types.ts/story';

type Props = {
  story?: Story;
};

export const Timeline: React.FC<Props> = observer(props => {
  const videoCreator = useVideoCreatorStore();
  const layoutStore = useLayoutStore();

  // Refs for DOM elements
  const scrollRef = useRef<HTMLDivElement>(null);
  const tracksDivRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const hasInitializedSelectedTrackRef = useRef(false);

  // Local state
  const [offsetY, setOffsetY] = useState(0);
  const [refScrollLeft, setRefScrollLeft] = useState<number>(0);
  const [selectedElement, setSelectedElement] = useState<ElementState | null>(
    null,
  );
  const refScrollLeftRef = useRef<number>(0);

  // Calculate dimensions
  const duration = videoCreator.duration;

  // Main timeline functionality through compound hook
  const {
    animatedRef,
    timelineRef,
    scale,
    handlePinch,
    currentTime,
    handleRepositionPlayheadClick,
    contextMenuState,
    showContextMenu,
    hideContextMenu,
    deleteActiveElement,
    splitActiveTrackElementAtCurrentTime,
    splitSceneAtPlayhead,
    rippleMode,
    setRippleMode,
    scrubToTime,
    maxWidth,
    expandScaleForDuration,
  } = useTimeline({
    duration,
    frameRate: videoCreator.timelineStore.framerate,
    enableKeyboardShortcuts: true,
    autoScroll: true,
  });

  // Track management
  const { tracks, activeTracks, trackHeight, getTrackInfo } =
    useTimelineTracks();

  const initialOriginalTrack = useMemo(() => {
    if (hasInitializedSelectedTrackRef.current) {
      return null;
    }

    const match = tracks.find(([, elements]) => {
      const firstElement = elements?.[0];
      if (!firstElement) return false;
      return videoCreator.isOriginalVideoElement(firstElement.source);
    });

    return match ?? null;
  }, [tracks, videoCreator]);

  useEffect(() => {
    if (!initialOriginalTrack) return;

    hasInitializedSelectedTrackRef.current = true;
    runInAction(() => {
      videoCreator.selectedTrack = initialOriginalTrack[0];
    });
  }, [initialOriginalTrack, videoCreator]);

  // Layout resize functionality (existing)
  const { handleMouseMove, handleMouseUp, handleMouseDown, isDragging } =
    useTimelineLayoutResize();

  // Sync refs
  useEffect(() => {
    if (timelineRef.current !== ref.current) {
      (timelineRef as any).current = ref.current;
    }
  }, []);

  // Calculate track width
  let trackWidth: number | undefined = duration * scale + 14;
  if (trackWidth < maxWidth - 14) {
    trackWidth = undefined;
  }

  // Calculate extra time width for timeline header
  const timeDiff = maxWidth - duration * scale;
  const extraTimeWidth = Math.max(0, timeDiff);

  // Mouse event handlers
  const mouseEnter = useCallback(() => {
    // Don't steal focus from the renderer iframe while the user is actively
    // editing on the canvas — focusing this scope container blurs the iframe
    // mid-drag, which trips the FORCED_REFRESH that reverts the in-progress
    // edit (PLAT-10700). The renderer canvas is the app's only iframe.
    if (document.activeElement instanceof HTMLIFrameElement) return;
    const currentScroll = window.scrollY;
    scrollRef.current?.focus();
    window.scrollTo({ top: currentScroll });
  }, []);

  const mouseLeave = useCallback(() => {
    scrollRef.current?.blur();
  }, []);

  useEffect(() => {
    // Ensure the timeline has focus on mount so keyboard shortcuts work immediately.
    mouseEnter();
  }, [mouseEnter]);

  // Element click handler for debug modal
  const handleElementClick = useCallback((element: ElementState) => {
    setSelectedElement(element);
  }, []);

  // Context menu handlers
  const onContextMenu = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
      element: ElementState,
    ) => {
      showContextMenu(e, element);
    },
    [showContextMenu],
  );

  const handlePasteContextMenu = useCallback(
    async (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
      track: number,
      elements: ElementState[],
    ) => {
      const { action, copied } = videoCreator.timelineClipboard || {};
      if (!action || !copied) return;

      const element = elements[0];
      if (element.source.type !== copied.element.source.type) {
        return;
      }

      const indent = tracksDivRef.current!.getBoundingClientRect().left;
      const posX = e.clientX + ref.current!.scrollLeft - indent;
      const time = (posX - 14) / scale;
      const isTaken = elements.some(
        e => time >= e.localTime && time <= e.localTime + e.duration,
      );
      if (isTaken) return;

      e.preventDefault();
      videoCreator.timelineClipboard = {
        ...videoCreator.timelineClipboard!,
        copied: {
          data: { time, track },
          element: videoCreator.timelineClipboard!.tmp.element,
        },
        pos: { left: e.clientX, top: e.clientY },
        action: 'paste',
      };
    },
    [videoCreator, scale],
  );

  // Scroll handling
  const handleScroll = useCallback(() => {
    if (ref.current && animatedRef.current) {
      animatedRef.current.style.left = `-${ref.current.scrollLeft}px`;
      setOffsetY(ref.current.scrollTop);
    }
  }, []);

  // Timeline click handler
  const onRepositionPlayheadClick = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      await handleRepositionPlayheadClick(e, tracksDivRef);
    },
    [handleRepositionPlayheadClick],
  );

  // Main click handler for clearing selections
  const onMainClick = useCallback(() => {
    hideContextMenu();
    if (videoCreator.selectedVolumeKeyPoint) {
      videoCreator.selectVolumeKeyPoint(null);
    }
  }, [hideContextMenu, videoCreator]);

  // Gesture binding - adapt the gesture state to match our hook interface
  const bind = usePinch(
    state => {
      const adaptedState = {
        offset: state.offset,
        scale: 1, // Use default scale since usePinch doesn't provide this
        velocity: Array.isArray(state.velocity) ? state.velocity[0] : 0,
        direction: state.direction,
        maxWidth,
        origin: state.origin,
        last: state.last,
      };
      handlePinch(adaptedState);
    },
    {
      preventDefault: false,
      scaleBounds: { min: 0.1, max: 10 },
      rubberband: true,
    },
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (refScrollLeftRef.current !== ref.current?.scrollLeft) {
        refScrollLeftRef.current = ref.current?.scrollLeft || 0;
        setRefScrollLeft(ref.current?.scrollLeft || 0);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (ref.current) {
      ref.current.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      if (ref.current) {
        ref.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  // During a story switch the editor still holds the previous story's project
  // for a beat (until the new renderVideo → loadProject), so its tracks —
  // filmstrip included — would flash before the new timeline. stateReady is
  // reset at switch start and re-latches on the new renderer state.
  const tracksReady = !!props.story && videoCreator.stateReady;

  return (
    <Main
      data-testid="creator-studio-timeline"
      data-shortcut-scope="timeline-stage"
      tabIndex={-1}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
      ref={scrollRef}
      style={{
        minHeight: layoutStore.timelineHeight,
        maxHeight: layoutStore.timelineHeight,
        height: layoutStore.timelineHeight,
      }}
      onClick={onMainClick}
    >
      <DraggableCore
        onDrag={e => {
          handleMouseMove(e as unknown as MouseEvent);
        }}
        onStop={handleMouseUp}
        onMouseDown={handleMouseDown}
      >
        <ResizeBorderHorizontal
          onMouseUp={handleMouseUp}
          isDragging={isDragging}
        />
      </DraggableCore>

      <TimelineControls
        deleteActiveElement={deleteActiveElement}
        splitActiveTrackElementAtCurrentTime={
          splitActiveTrackElementAtCurrentTime
        }
        rippleMode={rippleMode}
        setRippleMode={setRippleMode}
      />

      <Content {...bind()}>
        <TracksLeft>
          <Controls />
          <TimelineTrackLabels
            tracks={tracks}
            activeTracks={activeTracks}
            offsetY={offsetY}
            getTrackInfo={getTrackInfo}
          />
        </TracksLeft>

        <TracksRight ref={tracksDivRef} id="timeline-right">
          <TracksRightInner>
            <TracksRightTop>
              <TracksRightTopInner
                onClick={onRepositionPlayheadClick}
                ref={animatedRef}
              >
                <TimelineHeader
                  scale={scale}
                  duration={duration}
                  currentTime={currentTime}
                  maxWidth={maxWidth}
                  extraTimeWidth={extraTimeWidth}
                  refScrollLeft={refScrollLeft}
                  trackHeight={trackHeight}
                  scrubToTime={scrubToTime}
                  expandScaleForDuration={expandScaleForDuration}
                />
              </TracksRightTopInner>
            </TracksRightTop>

            <Tracks
              ref={ref}
              style={{ width: maxWidth - 14 }}
              id="timeline-scroll"
            >
              <TimelineTracks
                tracks={tracksReady ? tracks : []}
                activeTracks={activeTracks}
                scale={scale}
                trackWidth={trackWidth}
                onContextMenu={onContextMenu}
                onPasteContextMenu={handlePasteContextMenu}
                onElementClick={handleElementClick}
                onSplitSceneAtPlayhead={splitSceneAtPlayhead}
                contextMenuState={contextMenuState}
                story={props.story}
                getTrackInfo={getTrackInfo}
              />
            </Tracks>
          </TracksRightInner>
        </TracksRight>
      </Content>

      {selectedElement && (
        <DebugModal
          title="Timeline Element Debug"
          content={selectedElement}
          onClose={() => {
            setSelectedElement(null);
          }}
        />
      )}
    </Main>
  );
});

// Styled Components (keeping existing styles)
const Main = styled.div`
  position: relative;
  flex: 1 1 0%;
  /* Docked chrome sits one step above the stage, like the app sidebar; the
     tone change is the edge, so no rule ("Structural Lines" in
     docs/design-system.md). */
  background: var(--surface-subtle);
  display: flex;
  flex-direction: column;
  &:focus {
    outline: none;
  }
  z-index: 100000;
`;

const Content = styled.div`
  position: relative;
  display: flex;
  height: calc(100% - 45px);
  user-select: none;
  overflow: hidden;
  cursor: default;
`;

const TracksLeft = styled.div`
  width: 162px;
  position: relative;
`;

const Controls = styled.div`
  height: 35px;
  padding: 8px 5px 7px;
  display: flex;
  -webkit-box-align: center;
  align-items: center;
  box-sizing: border-box;
`;

const TracksRight = styled.div`
  position: relative;
  flex: 1 1 0%;
  height: 100%;
`;

const TracksRightInner = styled.div`
  position: relative;
  flex: 1 1 0%;
  height: 100%;
`;

const TracksRightTop = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const TracksRightTopInner = styled.div`
  position: absolute;
  height: 100%;
`;

const Tracks = styled.div`
  top: 35px;
  height: calc(100% - 35px);
  position: absolute;
  overflow: auto;
`;
